import { useAtomValue } from 'jotai';
import React, { useCallback, useEffect } from 'react';

import { Box, Select, Tooltip } from '~/components/blocks';
import { TemplateFragment } from '~/graphql/generated/graphql';
import { patientDetailDialogState } from '~/state/partials/patient_detail_dialog/atoms';

import { useFetchTemplates } from './use-fetch-templates';

type Props = {
  disabled?: boolean;
  patientName: string;
  onChange: (replacedMessage: string, template?: TemplateFragment) => void;
  selectedTemplateId: string;
};

/**
 * template中の `{{patientName}}` と `{{pharmacyName}}` を置換する
 *
 * variablesには patientName と pharmacyName というキーを持ったオブジェクトを渡す
 */
const replaceTemplateVariables = (tempalte: string, variables: Record<string, string>) => {
  return tempalte.replace(/\{\{(?:patientName|pharmacyName)\}\}/g, (match) => {
    const name = match.substr(2, match.length - 4);
    return variables[name] || match;
  });
};

export const SelectMessageTemplate = React.memo((props: Props) => {
  const { patientName, onChange } = props;

  const { templates, organizationName } = useFetchTemplates();
  const { recommendationPresetCode } = useAtomValue(patientDetailDialogState);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (!organizationName) return;

      const selectedTemplateId = e.currentTarget.value;
      const template = templates.find((template) => template.id === selectedTemplateId);

      if (template) {
        const message = replaceTemplateVariables(template.message, {
          patientName,
          pharmacyName: organizationName,
        });

        onChange(message, template);
      }
    },
    [onChange, organizationName, patientName, templates],
  );

  useEffect(() => {
    if (recommendationPresetCode && !props.selectedTemplateId && organizationName) {
      const template = templates.find(
        (template) => template.recommendationPresetCode === recommendationPresetCode,
      );

      if (template) {
        const message = replaceTemplateVariables(template.message, {
          patientName,
          pharmacyName: organizationName,
        });

        onChange(message, template);
      }
    }
  }, [
    onChange,
    organizationName,
    patientName,
    props.selectedTemplateId,
    recommendationPresetCode,
    templates,
  ]);

  return templates.length > 0 ? (
    <Select disabled={props.disabled} onChange={handleChange} fill value={props.selectedTemplateId}>
      <option hidden value="">
        テンプレートを選択
      </option>
      {templates
        .filter((template) => !template.recommendationPresetCode) // プリセット用テンプレートは画面には表示しない
        .map(({ id, subject }) => (
          <option key={id} value={id}>
            {subject}
          </option>
        ))}
    </Select>
  ) : (
    <Tooltip placement="bottom" content="テンプレートが登録されていないため追加できません">
      <Box>
        <Select disabled fill>
          <option hidden value="">
            テンプレートを選択
          </option>
        </Select>
      </Box>
    </Tooltip>
  );
});

SelectMessageTemplate.displayName = 'SelectMessageTemplate';
